<template>
  <Teleport :to="teleportTarget">
    <Transition name="auth-base-fade">
      <div v-if="modelValue" class="auth-base-mask" @click.self="onClose">
        <div class="auth-base-wrap">
          <Transition name="auth-base-pop">
            <dialog v-if="modelValue" class="auth-base-panel" open :aria-label="title">
              <header class="auth-base-head">
                <h2>{{ title }}</h2>
                <button type="button" class="auth-base-close" :aria-label="copy.close" @click="onClose">
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" />
                  </svg>
                </button>
              </header>

              <div class="auth-base-tabs" role="tablist" :aria-label="copy.tabsAria">
                <button
                  type="button"
                  role="tab"
                  class="auth-base-tab"
                  :class="{ 'auth-base-tab--active': mode === 'login' }"
                  :aria-selected="mode === 'login'"
                  @click="setMode('login')"
                >
                  {{ copy.loginTab }}
                </button>
                <button
                  type="button"
                  role="tab"
                  class="auth-base-tab"
                  :class="{ 'auth-base-tab--active': mode === 'register' }"
                  :aria-selected="mode === 'register'"
                  @click="setMode('register')"
                >
                  {{ copy.registerTab }}
                </button>
              </div>

              <form
                class="auth-base-form"
                :novalidate="disableBrowserValidation ? true : undefined"
                @submit.prevent="onSubmit"
              >
                <p v-if="mode === 'forgot'" class="auth-base-step-hint">
                  {{ copy.forgotStepHint }}
                </p>
                <div class="auth-base-field">
                  <label class="auth-base-label" :for="emailInputId">{{ copy.email }}</label>
                  <input
                    :id="emailInputId"
                    :value="model.email"
                    :type="disableBrowserValidation ? 'text' : 'email'"
                    autocomplete="email"
                    required
                    @input="onInput('email', $event)"
                  />
                </div>

                <template v-if="mode !== 'login'">
                  <div class="auth-base-field">
                    <label class="auth-base-label" :for="codeInputId">{{ copy.code }}</label>
                    <div class="auth-base-inline">
                      <input
                        :id="codeInputId"
                        :value="model.code"
                        type="text"
                        inputmode="numeric"
                        :maxlength="disableBrowserValidation ? undefined : 6"
                        required
                        @input="onInput('code', $event)"
                      />
                      <button
                        type="button"
                        class="auth-base-code-btn"
                        :disabled="sendingCode || cooldownSeconds > 0"
                        @click="onSendCodeClick"
                      >
                        {{ codeButtonText }}
                      </button>
                    </div>
                  </div>
                </template>

                <div class="auth-base-field">
                  <label class="auth-base-label" :for="passwordInputId">{{ passwordFieldLabel }}</label>
                  <div class="auth-base-password-wrap">
                    <input
                      :id="passwordInputId"
                      :value="model.password"
                      :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
                      :type="showPassword ? 'text' : 'password'"
                      :minlength="disableBrowserValidation ? undefined : 6"
                      required
                      @input="onInput('password', $event)"
                    />
                    <button
                      type="button"
                      class="auth-base-eye"
                      :aria-label="showPassword ? copy.hidePassword : copy.showPassword"
                      @click="togglePasswordVisible"
                    >
                      <svg v-if="showPassword" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path
                          d="M3 3l18 18M10.58 10.58A2 2 0 0 0 12 14a2 2 0 0 0 1.42-.58M9.36 5.38A10.9 10.9 0 0 1 12 5c5.45 0 9.27 4.16 10 7-.27 1.06-.98 2.4-2.14 3.63M6.4 6.4C4.63 7.67 3.4 9.48 3 12c.73 2.84 4.55 7 10 7 1.46 0 2.83-.28 4.03-.79"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.8"
                        />
                      </svg>
                      <svg v-else viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path
                          d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.8"
                        />
                      </svg>
                    </button>
                  </div>
                  <template v-if="mode !== 'login'">
                    <p class="auth-base-password-hint">{{ copy.passwordRuleHint }}</p>
                    <div class="auth-base-strength">
                      <div class="auth-base-strength__track">
                        <span
                          class="auth-base-strength__bar"
                          :class="passwordStrengthClass"
                          :style="{ width: `${Math.max(20, passwordStrengthScore * 25)}%` }"
                        />
                      </div>
                      <span class="auth-base-strength__label">{{ passwordStrengthLabel }}</span>
                    </div>
                  </template>
                </div>

                <p v-if="statusText" class="auth-base-status" :class="{ 'auth-base-status--error': statusIsError }">
                  {{ statusText }}
                </p>

                <button
                  type="submit"
                  class="auth-base-submit"
                  :class="{ 'auth-base-submit--primary': mode === 'login' }"
                  :disabled="submitting"
                >
                  {{ submitting ? copy.submitting : submitText }}
                </button>

                <div v-if="mode === 'login'" class="auth-base-login-links">
                  <button type="button" class="auth-base-login-links__btn" @click="setMode('register')">
                    {{ copy.goRegister }}
                  </button>
                  <span class="auth-base-login-links__divider" aria-hidden="true"></span>
                  <button type="button" class="auth-base-login-links__btn" @click="setMode('forgot')">
                    {{ copy.forgotPassword }}
                  </button>
                </div>

                <div
                  v-if="mode === 'login' && (googleOAuthVisible || githubOAuthVisible || wechatOAuthVisible)"
                  class="auth-base-social"
                >
                  <p class="auth-base-social__divider">{{ socialDividerText }}</p>
                  <div
                    v-if="googleOAuthVisible"
                    class="auth-base-social__row"
                  >
                    <img
                      v-if="googleIconSrc"
                      class="auth-base-social__mark"
                      :src="googleIconSrc"
                      width="22"
                      height="22"
                      alt=""
                      aria-hidden="true"
                    />
                    <div ref="gsiContainerRef" class="auth-base-social__gsi" />
                  </div>
                  <div
                    v-if="githubOAuthVisible"
                    class="auth-base-social__row"
                  >
                    <img
                      v-if="githubIconSrc"
                      class="auth-base-social__mark"
                      :src="githubIconSrc"
                      width="22"
                      height="22"
                      alt=""
                      aria-hidden="true"
                    />
                    <button type="button" class="auth-base-social__btn" @click="onGithubOAuthClick">
                      {{ copy.githubButton ?? 'GitHub' }}
                    </button>
                  </div>
                  <div
                    v-if="wechatOAuthVisible"
                    class="auth-base-social__row"
                  >
                    <span class="auth-base-social__mark auth-base-social__mark--wechat" aria-hidden="true">
                      <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" focusable="false">
                        <path
                          d="M669.3 369.4c9.8 0 19.6 0 29.4 1.6C671 245.2 536.9 152 383.2 152 211.6 152 71 269.7 71 416.8c0 85 45.8 156.9 124.2 210.9l-31.1 93.2L273.6 667c39.2 8.2 70.3 16.3 109.5 16.3 9.8 0 19.6 0 31.1-1.6-6.5-21.3-9.8-42.5-9.8-65.4 0.1-135.7 116.2-246.9 264.9-246.9z m-168.4-85c24.5 0 39.2 16.3 39.2 39.2 0 22.9-16.3 39.2-39.2 39.2-24.5 0-47.4-16.4-47.4-39.2 0-24.5 24.6-39.2 47.4-39.2z m-216.3 73.1c-24.7 0-47.8-16.2-47.8-38.8 0-24.3 24.7-38.8 47.8-38.8s39.5 16.2 39.5 38.8c0.1 22.7-16.4 38.8-39.5 38.8z"
                          fill="#24DB5A"
                        />
                        <path
                          d="M953.8 613c0-125.9-124.2-227.2-264.8-227.2-148.8 0-266.5 103-266.5 227.2 0 125.9 117.7 227.2 266.5 227.2 31.1 0 62.1-8.2 93.2-16.3l85 47.4-22.9-78.5c62.1-47.4 109.5-109.5 109.5-179.8z m-351.5-39.2c-14.7 0-31.1-14.7-31.1-31.1 0-14.7 16.3-31.1 31.1-31.1 22.9 0 39.2 16.3 39.2 31.1 0 16.4-14.7 31.1-39.2 31.1z m178-7.6c-14.8 0-31.3-14.6-31.3-30.7 0-14.6 16.5-30.7 31.3-30.7 23.1 0 39.5 16.2 39.5 30.7 0 16.2-16.4 30.7-39.5 30.7z"
                          fill="#24DB5A"
                        />
                      </svg>
                    </span>
                    <button type="button" class="auth-base-social__btn" @click="onWechatOAuthClick">
                      {{ copy.wechatButton ?? 'Continue with WeChat' }}
                    </button>
                  </div>
                </div>

                <p v-if="mode === 'forgot'" class="auth-base-switch-tip">
                  {{ copy.backToLoginPrefix }}
                  <button type="button" class="auth-base-switch-link" @click="setMode('login')">
                    {{ copy.backToLogin }}
                  </button>
                </p>
              </form>
            </dialog>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { createRandomUuid } from '../utils/randomUuid'
import {
  clearOAuthPopupOpenerOrigin,
  type OAuthPopupContext,
  isOAuthPopupMessage,
  openCenteredOAuthPopup,
  rememberOAuthPopupOpenerOrigin,
} from '../utils/oauthPopup'
import type { AuthSession } from './authSessionStore'

type AuthMode = 'login' | 'register' | 'forgot'
type AuthFieldKey = 'email' | 'code' | 'password'

interface AuthDialogCopy {
  loginTab: string
  registerTab: string
  loginTitle: string
  registerTitle: string
  forgotTitle: string
  tabsAria: string
  close: string
  email: string
  code: string
  password: string
  /** 找回密码模式下密码框标签（例如：`新密码`）。未提供时回退为 `password`。 */
  newPassword?: string
  sendCode: string
  submitting: string
  submitLogin: string
  submitRegister: string
  submitForgot: string
  noAccount: string
  goRegister: string
  forgotPassword: string
  backToLoginPrefix: string
  backToLogin: string
  showPassword: string
  hidePassword: string
  passwordRuleHint: string
  strengthWeak: string
  strengthMedium: string
  strengthStrong: string
  forgotStepHint: string
  /** 登录测试模式下的必填提示（可选，仅部分宿主使用）。 */
  testFillRequired?: string
  /**
   * 发码冷却中按钮文案模板，占位符为 `__N__`（例如 `__N__秒` / `__N__s`）。
   *
   * @remarks
   * 为兼容旧文案，运行时仍会回退替换 `{n}`。
   * 推荐使用 `__N__`，避免被 i18n 当作插值占位导致数字丢失。
   */
  cooldownSeconds?: string
  /** 「记住密码」勾选项文案（例如：`记住密码` / `Remember me`）。 */
  rememberMe?: string
  /** 免密天数下拉的无障碍标签（例如：`免密登录有效期`）。 */
  rememberDaysLabel?: string
  /**
   * 免密天数下拉选项文案模板，占位符为 `__DAYS__`（例如 `__DAYS__ 天` / `__DAYS__ days`）。
   *
   * @remarks
   * 勿使用 `{n}`：经 `vue-i18n` 的 `t()` 解析后会被当成插值而清空，导致界面只剩「天」等后缀。
   * 仍兼容在运行时对 `{n}` 的 `replaceAll`（旧文案）。
   */
  rememberDaysTpl?: string
  /** Google 登录区块上方说明（例如：`或通过以下方式继续`）。 */
  googleDivider?: string
  /** 多 OAuth 并存时优先于 `googleDivider` 的共用说明。 */
  socialDivider?: string
  /** GitHub 授权跳转按钮文案。 */
  githubButton?: string
  /** 微信快捷登录按钮文案。 */
  wechatButton?: string
  /** 微信 iframe 弹层标题。 */
  wechatPopupTitle?: string
}

interface AuthDialogModel {
  email: string
  code: string
  password: string
  /** 是否勾选「记住密码」。 */
  remember?: boolean
  /** 免密登录有效期（天）。允许 7 / 14 / 30。 */
  rememberDays?: number
}

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    mode: AuthMode
    model: AuthDialogModel
    copy: AuthDialogCopy
    statusText?: string
    statusIsError?: boolean
    cooldownSeconds?: number
    sendingCode?: boolean
    submitting?: boolean
    teleportTarget?: string
    /**
     * 为 true 时关闭浏览器约束校验（`novalidate`、放宽密码长度等），供测试登录模式使用。
     */
    disableBrowserValidation?: boolean
    /** 是否展示 Google 登录（由后端 `oauthConfig` / 站点初始化决定）。 */
    googleLoginEnabled?: boolean
    /** Google OAuth Web client_id。 */
    googleClientId?: string
    /** 品牌侧 Google 图标 URL（营销 / 编辑器分别传入）。 */
    googleIconSrc?: string
    /** 是否展示 GitHub 登录（由后端 `oauthConfig` 决定）。 */
    githubLoginEnabled?: boolean
    githubClientId?: string
    /** 与 GitHub OAuth App 登记的回调 URL 完全一致（含 path、无尾斜杠差异）。 */
    githubRedirectUri?: string
    githubIconSrc?: string
    /** 是否展示微信登录。 */
    wechatLoginEnabled?: boolean
    wechatAppId?: string
    wechatRedirectUri?: string
    /**
     * 点击「获取验证码」后执行的逻辑（打开人机验证、再请求发信等）。
     *
     * @remarks
     * 使用中性命名 `requestEmailCode`，避免 `*Handler` 等与运行时/类型推断冲突；
     * 支持同步或异步函数，返回值以 `Promise` 包裹后忽略拒绝态，避免未处理 rejection。
     */
    requestEmailCode?: () => void | Promise<void>
  }>(),
  {
    statusText: '',
    statusIsError: false,
    cooldownSeconds: 0,
    sendingCode: false,
    submitting: false,
    teleportTarget: 'body',
    disableBrowserValidation: false,
    googleLoginEnabled: false,
    googleClientId: '',
    googleIconSrc: '',
    githubLoginEnabled: false,
    githubClientId: '',
    githubRedirectUri: '',
    githubIconSrc: '',
    wechatLoginEnabled: false,
    wechatAppId: '',
    wechatRedirectUri: '',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'update:mode': [value: AuthMode]
  'update:model': [value: AuthDialogModel]
  submit: []
  sendCode: []
  /** 获取验证码前邮箱未填或格式不合法（由父层展示 `copy` 文案）。 */
  sendCodeRejected: [reason: 'empty_email' | 'invalid_email']
  /** Google Identity Services 返回的 JWT credential（id_token）。 */
  googleCredential: [credential: string]
  oauthSession: [session: AuthSession]
}>()

/** 表单控件 id 前缀，避免 `<label>` 嵌套按钮导致点击被浏览器吞掉。 */
const fieldId = useId()
const emailInputId = `${fieldId}-email`
const codeInputId = `${fieldId}-code`
const passwordInputId = `${fieldId}-password`
const showPassword = ref(false)
const gsiContainerRef = ref<HTMLElement | null>(null)
let gsiScriptPromise: Promise<void> | null = null
let wechatPopupWindow: Window | null = null
let wechatPopupCloseTimer: number | null = null

const mode = computed(() => props.mode)
const teleportTarget = computed(() => props.teleportTarget)

const googleOAuthVisible = computed(
  () => props.mode === 'login' && props.googleLoginEnabled && Boolean(props.googleClientId?.trim()),
)

const githubOAuthVisible = computed(
  () =>
    props.mode === 'login' &&
    props.githubLoginEnabled &&
    Boolean(props.githubClientId?.trim()) &&
    Boolean(props.githubRedirectUri?.trim()),
)

const wechatOAuthVisible = computed(
  () =>
    props.mode === 'login' &&
    props.wechatLoginEnabled &&
    Boolean(props.wechatAppId?.trim()) &&
    Boolean(props.wechatRedirectUri?.trim()),
)

const socialDividerText = computed(() => props.copy.socialDivider?.trim() || props.copy.googleDivider || '')
const passwordStrengthScore = computed(() => evaluatePasswordStrength(props.model.password))
const passwordStrengthLabel = computed(() => {
  if (passwordStrengthScore.value >= 3) return props.copy.strengthStrong
  if (passwordStrengthScore.value >= 2) return props.copy.strengthMedium
  return props.copy.strengthWeak
})
const passwordStrengthClass = computed(() => {
  if (passwordStrengthScore.value >= 3) return 'auth-base-strength--strong'
  if (passwordStrengthScore.value >= 2) return 'auth-base-strength--medium'
  return 'auth-base-strength--weak'
})

const title = computed(() => {
  if (props.mode === 'register') return props.copy.registerTitle
  if (props.mode === 'forgot') return props.copy.forgotTitle
  return props.copy.loginTitle
})

/**
 * 密码字段标签：找回密码时优先展示「新密码」。
 */
const passwordFieldLabel = computed(() =>
  props.mode === 'forgot' ? (props.copy.newPassword ?? props.copy.password) : props.copy.password,
)

const submitText = computed(() => {
  if (props.mode === 'register') return props.copy.submitRegister
  if (props.mode === 'forgot') return props.copy.submitForgot
  return props.copy.submitLogin
})

const codeButtonText = computed(() => {
  if (props.cooldownSeconds > 0) {
    const tpl = props.copy.cooldownSeconds ?? '__N__s'
    return tpl.replaceAll('__N__', String(props.cooldownSeconds)).replaceAll('{n}', String(props.cooldownSeconds))
  }
  return props.copy.sendCode
})

/** 免密登录可选天数（与后端 keep_days 对齐）。 */
const REMEMBER_DAYS_OPTIONS = [7, 14, 30] as const

type RememberDayOption = (typeof REMEMBER_DAYS_OPTIONS)[number]

/** 默认免密天数（与宿主 `formModel.rememberDays` 初始值一致）。 */
const DEFAULT_REMEMBER_DAYS: RememberDayOption = 30

/**
 * 关闭弹窗。
 */
function onClose(): void {
  showPassword.value = false
  cancelGoogleOneTap()
  cleanupWechatPopupState()
  emit('update:modelValue', false)
}

/**
 * 加载 Google Identity Services 客户端脚本（单例）。
 */
function loadGsiScript(): Promise<void> {
  if (typeof globalThis.window === 'undefined') return Promise.resolve()
  const w = globalThis.window as Window & { google?: { accounts?: { id?: unknown } } }
  if (w.google?.accounts?.id) return Promise.resolve()
  if (!gsiScriptPromise) {
    gsiScriptPromise = new Promise<void>((resolve, reject) => {
      const s = document.createElement('script')
      s.src = 'https://accounts.google.com/gsi/client'
      s.async = true
      s.defer = true
      s.onload = () => resolve()
      s.onerror = () => reject(new Error('gsi_script_load_failed'))
      document.head.appendChild(s)
    })
  }
  return gsiScriptPromise
}

/**
 * 关闭 One Tap / FedCM 提示，避免弹窗关闭后仍残留。
 */
function cancelGoogleOneTap(): void {
  if (typeof globalThis.window === 'undefined') return
  const w = globalThis.window as Window & { google?: { accounts?: { id?: { cancel?: () => void } } } }
  try {
    w.google?.accounts?.id?.cancel?.()
  } catch {
    /* ignore */
  }
}

/**
 * 在容器内渲染「使用 Google 账号登录」按钮。
 */
async function mountGoogleButton(): Promise<void> {
  if (!googleOAuthVisible.value) return
  try {
    await loadGsiScript()
  } catch {
    return
  }
  await nextTick()
  const el = gsiContainerRef.value
  if (!el) return
  el.innerHTML = ''
  const w = globalThis.window as Window & {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: Record<string, unknown>) => void
          renderButton: (target: HTMLElement, opts: Record<string, unknown>) => void
        }
      }
    }
  }
  const idApi = w.google?.accounts?.id
  if (!idApi) return
  idApi.initialize({
    client_id: props.googleClientId,
    callback: (res: { credential?: string }) => {
      const c = res?.credential
      if (c) emit('googleCredential', c)
    },
  })
  const width = Math.max(240, el.clientWidth || 280)
  idApi.renderButton(el, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    width: String(width),
    text: 'continue_with',
    locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
  })
}

watch(
  () =>
    [
      props.modelValue,
      props.mode,
      props.googleLoginEnabled,
      props.googleClientId,
      props.githubLoginEnabled,
      props.githubClientId,
      props.githubRedirectUri,
      props.wechatLoginEnabled,
      props.wechatAppId,
      props.wechatRedirectUri,
    ] as const,
  () => {
    mountGoogleButton().catch(() => undefined)
  },
  { flush: 'post' },
)

/**
 * 跳转 GitHub 授权页（授权码流程）；`state` 与 `redirect_uri` 在回调页与换票时校验。
 */
function onGithubOAuthClick(): void {
  if (typeof globalThis.window === 'undefined') return
  const cid = props.githubClientId?.trim() ?? ''
  const redir = props.githubRedirectUri?.trim() ?? ''
  if (!cid || !redir) return
  const state = createRandomUuid().replace(/-/g, '').slice(0, 32)
  try {
    sessionStorage.setItem('github_oauth_state', state)
    sessionStorage.setItem('github_oauth_redirect_uri', redir)
    sessionStorage.setItem('github_oauth_keep', '1')
    const days = REMEMBER_DAYS_OPTIONS.includes(Number(props.model.rememberDays) as RememberDayOption)
      ? Number(props.model.rememberDays)
      : DEFAULT_REMEMBER_DAYS
    sessionStorage.setItem('github_oauth_keep_days', String(days))
  } catch {
    return
  }
  cancelGoogleOneTap()
  cleanupWechatPopupState()
  const u = new URL('https://github.com/login/oauth/authorize')
  u.searchParams.set('client_id', cid)
  u.searchParams.set('redirect_uri', redir)
  u.searchParams.set('scope', 'read:user user:email')
  u.searchParams.set('state', state)
  globalThis.window.location.href = u.toString()
}

function onWechatOAuthClick(): void {
  if (typeof globalThis.window === 'undefined') return
  const appId = props.wechatAppId?.trim() ?? ''
  const redirectUri = props.wechatRedirectUri?.trim() ?? ''
  if (!appId || !redirectUri) return
  const state = createRandomUuid().replace(/-/g, '').slice(0, 32)
  const days = REMEMBER_DAYS_OPTIONS.includes(Number(props.model.rememberDays) as RememberDayOption)
    ? Number(props.model.rememberDays)
    : DEFAULT_REMEMBER_DAYS
  try {
    sessionStorage.setItem('wechat_oauth_state', state)
    sessionStorage.setItem('wechat_oauth_redirect_uri', redirectUri)
    sessionStorage.setItem('wechat_oauth_keep', '1')
    sessionStorage.setItem('wechat_oauth_keep_days', String(days))
  } catch {
    return
  }
  cancelGoogleOneTap()
  cleanupWechatPopupState()
  rememberOAuthPopupOpenerOrigin()
  const context: OAuthPopupContext = {
    provider: 'wechat',
    openerOrigin: globalThis.window.location.origin,
    state,
    redirectUri,
    keep: true,
    keepDays: days,
  }
  const url = new URL('https://open.weixin.qq.com/connect/qrconnect')
  url.searchParams.set('appid', appId)
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('scope', 'snsapi_login')
  url.searchParams.set('state', state)
  const popup = openCenteredOAuthPopup(`${url.toString()}#wechat_redirect`, 'wechat', { width: 720, height: 720 }, context)
  if (!popup) {
    clearOAuthPopupOpenerOrigin()
    return
  }
  wechatPopupWindow = popup
  popup.focus()
  wechatPopupCloseTimer = globalThis.window.setInterval(() => {
    if (!wechatPopupWindow?.closed) return
    cleanupWechatPopupState()
  }, 500)
}

function cleanupWechatPopupState(): void {
  if (wechatPopupCloseTimer) {
    globalThis.clearInterval(wechatPopupCloseTimer)
    wechatPopupCloseTimer = null
  }
  if (wechatPopupWindow && !wechatPopupWindow.closed) {
    wechatPopupWindow.close()
  }
  wechatPopupWindow = null
}

function onWechatOAuthMessage(ev: MessageEvent): void {
  if (!isOAuthPopupMessage(ev.data)) return
  const payload = ev.data
  if (payload.provider !== 'wechat') return
  cleanupWechatPopupState()
  clearOAuthPopupOpenerOrigin()
  if (payload.type === 'success') {
    emit('oauthSession', payload.session)
    emit('update:modelValue', false)
  }
}

watch(
  () => props.modelValue,
  (open: boolean) => {
    if (open) {
      emit('update:model', { ...props.model, remember: true, rememberDays: DEFAULT_REMEMBER_DAYS })
      return
    }
    cancelGoogleOneTap()
    cleanupWechatPopupState()
  },
)

onBeforeUnmount(() => {
  cancelGoogleOneTap()
  cleanupWechatPopupState()
  clearOAuthPopupOpenerOrigin()
  if (typeof globalThis.window !== 'undefined') {
    globalThis.window.removeEventListener('message', onWechatOAuthMessage)
  }
})

if (typeof globalThis.window !== 'undefined') {
  globalThis.window.addEventListener('message', onWechatOAuthMessage)
}

/**
 * 更新登录/注册/找回密码模式。
 *
 * @param nextMode - 目标模式
 */
function setMode(nextMode: AuthMode): void {
  showPassword.value = false
  emit('update:mode', nextMode)
}

/**
 * 切换密码显隐状态。
 */
function togglePasswordVisible(): void {
  showPassword.value = !showPassword.value
}

/**
 * 输入字段变更统一派发。
 *
 * @param key - 字段名
 * @param ev - 输入事件
 */
function onInput(key: AuthFieldKey, ev: Event): void {
  const next = { ...props.model }
  const value = (ev.target as HTMLInputElement | null)?.value ?? ''
  next[key] = key === 'password' ? value : value.trim()
  emit('update:model', next)
}

/**
 * 触发表单提交。
 */
function onSubmit(): void {
  emit('submit')
}

/**
 * 校验邮箱是否满足发验证码前的格式要求（非测试模式）。
 *
 * @param email - 已 trim 的邮箱
 */
function isEmailFormatValidForSendCode(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

/**
 * 安全调用父层传入的发码流程（兼容 async，并避免非函数误调）。
 *
 * @param fn - 父层回调
 * @returns 是否已调用回调
 */
function invokeRequestEmailCode(fn: unknown): boolean {
  if (fn == null) return false
  if (typeof fn !== 'function') return false
  void Promise.resolve((fn as () => unknown)()).catch(() => undefined)
  return true
}

/**
 * 「获取验证码」：先做邮箱预校验，再调用 `requestEmailCode` 或派发 `sendCode`。
 *
 * @param ev - 点击事件
 */
function onSendCodeClick(ev: MouseEvent): void {
  ev.preventDefault()
  ev.stopPropagation()
  const raw = props.model.email ?? ''
  const email = raw.trim()

  if (props.disableBrowserValidation) {
    if (!email) {
      emit('sendCodeRejected', 'empty_email')
      return
    }
  } else {
    if (!email) {
      emit('sendCodeRejected', 'empty_email')
      return
    }
    if (!isEmailFormatValidForSendCode(email)) {
      emit('sendCodeRejected', 'invalid_email')
      return
    }
  }

  if (invokeRequestEmailCode(props.requestEmailCode)) return
  emit('sendCode')
}

/**
 * 评估密码强度（0-4）。
 *
 * @param value - 当前密码
 * @returns 强度分值
 */
function evaluatePasswordStrength(value: string): number {
  let score = 0
  if (value.length >= 8) score += 1
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1
  if (/\d/.test(value)) score += 1
  if (/[^A-Za-z0-9]/.test(value)) score += 1
  return score
}
</script>

<style scoped>
.auth-base-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgb(2 6 23 / 66%);
  padding: 1rem;
}

.auth-base-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 认证面板本地品牌色：与历史渐变主按钮 / 文字链接一致 */
.auth-base-panel {
  position: relative;
  inset: auto;
  left: auto;
  right: auto;
  width: min(440px, calc(100vw - 2rem));
  max-height: calc(100dvh - 2rem);
  overflow: auto;
  margin: 0 auto;
  padding: 1rem;
  border-radius: 14px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  box-shadow: var(--mkt-shadow, 0 18px 60px rgb(15 23 42 / 20%));
  --auth-brand: #1f7ab8;
  --auth-brand-deep: #156f97;
}

.auth-base-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.auth-base-head h2 {
  margin: 0;
  font-size: 1.15rem;
  letter-spacing: 0.01em;
}

.auth-base-close {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  color: var(--mkt-color-text, #0f172a);
  cursor: pointer;
  display: grid;
  place-items: center;
}

.auth-base-tabs {
  margin-top: 0.95rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}

.auth-base-tab {
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-bg, #f8fafc);
  color: var(--mkt-color-muted, #526077);
  cursor: pointer;
  font-weight: 600;
}

.auth-base-tab--active {
  color: var(--mkt-color-text, #0f172a);
  border-color: var(--im-border-strong, #8ca2c1);
  background: color-mix(in srgb, var(--im-accent-ghost, #e5efff) 72%, var(--mkt-color-bg, #f8fafc));
}

.auth-base-form {
  margin-top: 1rem;
  display: grid;
  gap: 0.82rem;
}

.auth-base-field {
  display: grid;
  gap: 0.35rem;
}

.auth-base-label {
  font-size: 0.86rem;
  color: var(--mkt-color-muted, #526077);
  font-weight: inherit;
  cursor: pointer;
}

.auth-base-step-hint {
  margin: 0;
  padding: 0.55rem 0.65rem;
  border-radius: 8px;
  border: 1px dashed var(--mkt-color-border, #d6deea);
  color: var(--mkt-color-muted, #526077);
  background: color-mix(in srgb, var(--mkt-color-bg, #f8fafc) 75%, #ffffff);
  font-size: 0.82rem;
}

.auth-base-field input {
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-bg, #f8fafc);
  color: var(--mkt-color-text, #0f172a);
  padding: 0 0.72rem;
}

.auth-base-inline {
  display: grid;
  grid-template-columns: 1fr 118px;
  gap: 0.55rem;
}

.auth-base-code-btn {
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  color: var(--mkt-color-text, #0f172a);
  cursor: pointer;
}

.auth-base-password-wrap {
  position: relative;
}

.auth-base-password-wrap input {
  width: 100%;
  padding-right: 2.6rem;
}

.auth-base-eye {
  position: absolute;
  right: 0.45rem;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--mkt-color-muted, #526077);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.auth-base-password-hint {
  margin: 0.45rem 0 0;
  color: var(--mkt-color-muted, #526077);
  font-size: 0.78rem;
  line-height: 1.45;
}

.auth-base-strength {
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.auth-base-strength__track {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.auth-base-strength__bar {
  display: block;
  height: 100%;
  border-radius: inherit;
  transition: width 200ms ease, background-color 200ms ease;
}

.auth-base-strength--weak {
  background: #ef4444;
}

.auth-base-strength--medium {
  background: #f59e0b;
}

.auth-base-strength--strong {
  background: #10b981;
}

.auth-base-strength__label {
  font-size: 0.76rem;
  color: var(--mkt-color-muted, #526077);
}

/**
 * 登录态「记住密码」复选框：圆角方框 + 白色对勾，勾选后填充历史品牌蓝。
 */
.auth-base-remember {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--mkt-color-text, #0f172a);
  cursor: pointer;
  user-select: none;
}

.auth-base-remember__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.auth-base-remember__box {
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid var(--mkt-color-border, #c9d4e4);
  background: var(--mkt-color-surface, #ffffff);
  color: transparent;
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    color 160ms ease;
}

.auth-base-remember__input:checked + .auth-base-remember__box {
  background: var(--auth-brand);
  border-color: var(--auth-brand);
  color: #fff;
}

.auth-base-remember__input:focus-visible + .auth-base-remember__box {
  outline: none;
  box-shadow: var(--mkt-focus-ring, 0 0 0 3px rgb(59 130 246 / 35%));
}

.auth-base-remember__text {
  line-height: 1.25;
}

/** 免密登录分段选择：`<fieldset>` + 三枚胶囊按钮，选中态与主按钮同色阶。 */
.auth-base-keep {
  margin: 0;
  padding: 0;
  border: 0;
  display: grid;
  gap: 0.5rem;
}

.auth-base-keep__label {
  padding: 0;
  padding-bottom: 0.2rem;
  font-size: 0.88rem;
  color: var(--mkt-color-muted, #526077);
}

.auth-base-keep__segments {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.65rem;
}

.auth-base-keep__seg {
  min-height: 32px;
  padding: 0 0.5rem;
  border-radius: 999px;
  border: 1.5px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  color: var(--mkt-color-muted, #526077);
  font-size: 0.92rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    color 160ms ease,
    box-shadow 160ms ease;
}

.auth-base-keep__seg:hover:not(:disabled):not(.auth-base-keep__seg--active) {
  border-color: color-mix(in srgb, var(--auth-brand) 50%, transparent);
  color: var(--mkt-color-text, #0f172a);
}

.auth-base-keep__seg--active {
  background: linear-gradient(135deg, var(--auth-brand-deep), var(--auth-brand));
  border-color: var(--auth-brand);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 6px 16px -8px rgb(31 122 184 / 45%);
}

.auth-base-keep__seg:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.auth-base-keep--disabled .auth-base-keep__label {
  opacity: 0.6;
}

.auth-base-submit {
  min-height: 48px;
  border-radius: 12px;
  border: 0;
  background: linear-gradient(135deg, #156f97, #1f7ab8);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

/** 登录模式主按钮：尺寸略大，配色与历史渐变提交按钮一致。 */
.auth-base-submit--primary {
  min-height: 52px;
  margin-top: 0.35rem;
  border-radius: 14px;
  font-size: 1rem;
  letter-spacing: 0.04em;
  background: linear-gradient(135deg, var(--auth-brand-deep), var(--auth-brand));
  box-shadow: 0 12px 24px -12px rgb(31 122 184 / 45%);
  transition:
    filter 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;
}

.auth-base-submit--primary:hover:not(:disabled) {
  filter: brightness(0.96);
}

.auth-base-submit--primary:active:not(:disabled) {
  transform: translateY(1px);
}

/** 登录页底部两个文本链接（注册账号 / 忘记密码）居中分隔排布。 */
.auth-base-login-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  margin-top: -0.15rem;
}

.auth-base-login-links__btn {
  border: 0;
  background: transparent;
  padding: 0.35rem 0.2rem;
  color: var(--auth-brand);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.auth-base-login-links__btn:hover {
  text-decoration: underline;
}

.auth-base-login-links__divider {
  width: 1px;
  height: 14px;
  background: var(--mkt-color-border, #d6deea);
}

.auth-base-social {
  margin-top: 0.35rem;
  display: grid;
  gap: 0.55rem;
}

.auth-base-social__divider {
  margin: 0;
  text-align: center;
  font-size: 0.82rem;
  color: var(--mkt-color-muted, #526077);
}

.auth-base-social__row {
  display: flex;
  align-items: stretch;
  gap: 0.55rem;
}

.auth-base-social__mark {
  flex: 0 0 auto;
  align-self: center;
  border-radius: 6px;
}

.auth-base-social__mark--wechat {
  display: grid;
  place-items: center;
}

.auth-base-social__mark--wechat svg {
  width: 22px;
  height: 22px;
  display: block;
}

.auth-base-social__gsi {
  flex: 1 1 auto;
  min-width: 0;
}

.auth-base-social__gsi :deep(iframe) {
  width: 100% !important;
}

.auth-base-social__btn {
  flex: 1 1 auto;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  color: var(--mkt-color-text, #0f172a);
  font-weight: 600;
  font-size: 0.92rem;
  cursor: pointer;
}

.auth-base-social__btn:hover {
  border-color: var(--im-border-strong, #8ca2c1);
  background: var(--mkt-color-bg, #f8fafc);
}

.auth-base-code-btn:disabled,
.auth-base-submit:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

.auth-base-status {
  margin: 0;
  font-size: 0.85rem;
  color: #0f766e;
}

.auth-base-status--error {
  color: #b91c1c;
}

.auth-base-switch-tip {
  margin: 0;
  text-align: center;
  color: var(--mkt-color-muted, #526077);
  font-size: 0.86rem;
}

.auth-base-switch-link {
  border: 0;
  padding: 0;
  margin-left: 0.3rem;
  background: transparent;
  color: #1f7ab8;
  cursor: pointer;
  font-weight: 700;
}

.auth-base-switch-link:hover {
  text-decoration: underline;
}

.auth-base-field input:focus-visible,
.auth-base-code-btn:focus-visible,
.auth-base-submit:focus-visible,
.auth-base-tab:focus-visible,
.auth-base-close:focus-visible,
.auth-base-switch-link:focus-visible,
.auth-base-login-links__btn:focus-visible,
.auth-base-eye:focus-visible,
.auth-base-keep__seg:focus-visible,
.auth-base-social__btn:focus-visible {
  outline: none;
  box-shadow: var(--mkt-focus-ring, 0 0 0 3px rgb(59 130 246 / 35%));
}

.auth-base-fade-enter-active,
.auth-base-fade-leave-active {
  transition: opacity 220ms ease;
}

.auth-base-fade-enter-from,
.auth-base-fade-leave-to {
  opacity: 0;
}

.auth-base-pop-enter-active,
.auth-base-pop-leave-active {
  transition:
    opacity 260ms ease,
    transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.auth-base-pop-enter-from,
.auth-base-pop-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .auth-base-fade-enter-active,
  .auth-base-fade-leave-active,
  .auth-base-pop-enter-active,
  .auth-base-pop-leave-active {
    transition: none;
  }
}
</style>
